import React, { useState, useEffect, Suspense } from "react";
import { ThemeProvider } from "@mui/material/styles";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import { basePath, hostLoginURL, isHostAuth, stripBase, withBase } from "./runtimeConfig";
import { hostSignInURL, requestedReturnPath, studioLoginPath } from "./admin/utils/authRedirect";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SuccessBanner from "./admin/components/common/SuccessBanner";

// Configurations and utilities
import { loadConfig, getConfig } from "./config";
import { reinitializeApiClient } from "./admin/utils/apiClient";
import { reinitializePubClient } from "./admin/utils/pubClient";
import pubClient from "./admin/utils/pubClient";

// Themes
import { generateTheme } from "./admin/theme";

// Pages (add OAuthConsentPage)
import OAuthConsentPage from "./portal/pages/OAuthConsentPage";

// Components

// Layouts
import MainLayout from "./layouts/MainLayout";

// Context providers
import { NotificationProvider } from "./admin/context/NotificationContext";
import { EditionProvider } from "./admin/context/EditionContext";
import { SyncStatusProvider } from "./admin/context/SyncStatusContext";
import { PermissionsProvider } from "./admin/context/PermissionsContext";
import { setIdentity, clearIdentity } from "./admin/utils/identityStore";
import { installMultiSelectClickThrough } from "./admin/utils/multiSelectClickThrough";

// Lazy loaded routes for code splitting
const AdminRoutes = React.lazy(() => import("./routes/AdminRoutes"));
const PortalRoutes = React.lazy(() => import("./routes/PortalRoutes"));
const ChatRoutes = React.lazy(() => import("./routes/ChatRoutes"));
const Login = React.lazy(() => import("./portal/pages/Login"));
const Register = React.lazy(() => import("./portal/pages/Register"));
const ForgotPassword = React.lazy(() => import("./portal/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./portal/pages/ResetPassword"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const ToolDocumentationPage = React.lazy(() => import("./portal/pages/ToolDocumentationPage"));

// Sends a signed-out visitor to sign in: to the host application's sign-in
// page when it authenticates users (with the page they asked for, when the
// login URL takes it), else to Studio's login route with the page they asked
// for as ?next=, so signing in lands there instead of on the dashboard.
const SignInRedirect = () => {
  const location = useLocation();
  if (isHostAuth() && hostLoginURL()) {
    window.location.assign(hostSignInURL());
    return <CircularProgress />;
  }
  return <Navigate to={studioLoginPath(location)} replace />;
};

// Where a signed-in user who opens /login goes: the page named by ?next=
// when it is a safe console route, else their home surface.
const SignedInLoginRedirect = ({ entitlements }) => {
  const location = useLocation();
  const home =
    entitlements?.is_full_admin || entitlements?.has_admin_access ? "/admin" : "/portal/dashboard";
  return <Navigate to={requestedReturnPath(location.search) || home} replace />;
};

// Component to redirect OAuth requests to backend
const BackendRedirect = () => {
  const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
  const currentPath = window.location.pathname;
  const queryString = window.location.search;
  
  // Redirect to backend URL
  window.location.href = `${backendUrl}${currentPath}${queryString}`;
  
  return <CircularProgress />;
};

// Loading component for lazy-loaded routes
const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "50vh",
    }}
  >
    <CircularProgress />
  </Box>
);

// Registration and password reset are Studio's own accounts, which are off
// when the host application signs users in (their APIs answer 404): those
// routes then lead to the console's home, and from there to sign-in.
const LocalAccountRoute = ({ isAuthenticated, children }) => {
  if (isHostAuth()) {
    return <Navigate to="/" replace />;
  }
  return isAuthenticated ? (
    <Navigate to="/portal/dashboard" replace />
  ) : (
    <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [me, setMe] = useState(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(true);
  const [theme, setTheme] = useState(generateTheme()); // Dynamic theme
  const [customCSS, setCustomCSS] = useState(''); // Custom CSS

  const handleCloseBanner = () => {
    setShowSuccessBanner(false);
  };

  // A click outside an open multi-select closes it and still reaches its
  // target, app-wide (#502 E6).
  useEffect(() => installMultiSelectClickThrough(), []);

  useEffect(() => {
    const initialize = async () => {
      try {
        await loadConfig();
        reinitializeApiClient();
        reinitializePubClient();

        // Apply branding configuration
        const config = getConfig();
        if (config.branding) {
          // Generate dynamic theme with custom colors
          const brandedTheme = generateTheme(config.branding);
          setTheme(brandedTheme);

          // Set custom CSS if provided
          if (config.branding.custom_css) {
            setCustomCSS(config.branding.custom_css);
          }

          // Update document title if custom app title is set
          if (config.branding.app_title) {
            document.title = config.branding.app_title;
          }

          // Update favicon if custom favicon is available
          if (config.branding.has_custom_favicon) {
            const faviconLink = document.querySelector("link[rel='icon']") || document.createElement('link');
            faviconLink.rel = 'icon';
            faviconLink.href = withBase('/api/v1/branding/favicon');
            if (!document.querySelector("link[rel='icon']")) {
              document.head.appendChild(faviconLink);
            }
          }
        }

        setConfigLoaded(true);

        // Skip auth check for password reset and forgot password routes
        // (Studio's own accounts; with host sign-in they redirect instead)
        const currentPath = stripBase(window.location.pathname);
        if (!isHostAuth() && (currentPath === '/register' ||
          currentPath === '/reset-password' ||
          currentPath === '/auth/reset-password' ||
          currentPath === '/forgot-password' ||
          currentPath === '/auth/forgot-password')) {
          setIsAuthenticated(false);
          return;
        }

        try {
          const response = await pubClient.get("/common/me");
          setIsAuthenticated(true);
          const me = response.data;
          const attributes = me.attributes;
          // One identity fetch for the whole app: the store feeds the
          // permissions context, the layouts and the non-React consumers.
          const identity = setIdentity(me);
          setMe(me);
          setEntitlements({
            is_admin: attributes.is_admin,
            has_admin_access: identity.hasAdminAccess,
            is_full_admin: identity.isFullAdmin,
            ui_options: attributes.ui_options,
            entitlements: attributes.entitlements,
          });
        } catch (authError) {
          clearIdentity();
          if (authError.response && authError.response.status === 401) {
            setIsAuthenticated(false);
          } else {
            console.error("Authentication check failed:", authError);
            setError("Failed to check authentication status.");
          }
        }
      } catch (error) {
        console.error("Configuration initialization failed:", error);
        setError("Failed to initialize application configuration.");
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, []);

  if (loading || !configLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>{error}</div>
      </Box>
    );
  }

  return (
    <Router basename={basePath() || undefined}>
      <EditionProvider>
        <PermissionsProvider identity={me}>
        <SyncStatusProvider>
        <NotificationProvider enabled={isAuthenticated}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
          {/* Inject custom CSS if provided */}
          {customCSS && (
            <style dangerouslySetInnerHTML={{ __html: customCSS }} />
          )}
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <SignedInLoginRedirect entitlements={entitlements} />
                ) : (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <Login />
                  </Suspense>
                )
              }
            />
            <Route
              path="/register"
              element={
                <LocalAccountRoute isAuthenticated={isAuthenticated}>
                  <Register />
                </LocalAccountRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <LocalAccountRoute isAuthenticated={isAuthenticated}>
                  <ForgotPassword />
                </LocalAccountRoute>
              }
            />
            {/* Handle both /reset-password and /auth/reset-password */}
            <Route
              path="/reset-password"
              element={
                <LocalAccountRoute isAuthenticated={isAuthenticated}>
                  <ResetPassword />
                </LocalAccountRoute>
              }
            />
            <Route
              path="/auth/reset-password"
              element={<Navigate to="/reset-password" replace state={{ preserveQuery: true }} />}
            />

            {/* OAuth Consent Page Route - public layout, backend handles auth check */}
            <Route path="/oauth/consent" element={<OAuthConsentPage />} />
            
            {/* OAuth Backend Routes - redirect to backend to handle these */}
            <Route path="/oauth/authorize" element={<BackendRedirect />} />
            <Route path="/oauth/token" element={<BackendRedirect />} />
            <Route path="/oauth/register_client" element={<BackendRedirect />} />
            <Route path="/.well-known/oauth-authorization-server" element={<BackendRedirect />} />


            {/* Protected Routes with MainLayout */}
            <Route
              element={
                isAuthenticated ? (
                  <MainLayout />
                ) : (
                  <SignInRedirect />
                )
              }
            >
              {/* Portal Routes */}
              <Route path="/portal/*" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <PortalRoutes />
                </Suspense>
              } />

              {/* Chat Routes */}
              <Route path="/chat/*" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <ChatRoutes />
                </Suspense>
              } />

              {/* Admin Routes — guarded: users without any admin permission are redirected */}
              <Route path="/admin/*" element={
                entitlements?.has_admin_access ? (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <AdminRoutes uiOptions={entitlements?.ui_options} />
                  </Suspense>
                ) : (
                  <Navigate to="/" replace />
                )
              } />

              {/* Common Routes */}
              <Route path="/notifications" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <NotificationsPage />
                </Suspense>
              } />
              <Route path="/common/tools/:id/docs" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <ToolDocumentationPage />
                </Suspense>
              } />

              {/* Default redirect */}
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    entitlements?.is_full_admin === true ? (
                      <Navigate to="/admin" replace />
                    ) : entitlements?.has_admin_access ? (
                      <Navigate to="/admin" replace />
                    ) : entitlements?.ui_options?.show_portal ? (
                      <Navigate to="/portal/dashboard" replace />
                    ) : entitlements?.ui_options?.show_chat ? (
                      <Navigate to="/chat/dashboard" replace />
                    ) : (
                      <Box sx={{ p: 7, display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography variant="headingXLarge">
                          Welcome to Tyk AI Studio!
                        </Typography>
                        {showSuccessBanner && (
                          <SuccessBanner
                            title="Tyk AI studio account"
                            message="You'll receive an email once your role is assigned and access is ready. If there's a delay, contact your organization admin"
                            onClose={handleCloseBanner}
                          />
                        )}
                      </Box>
                    )
                  ) : (
                    <SignInRedirect />
                  )
                }
              />
            </Route>

            {/* Catch all route */}
            <Route
              path="*"
              element={
                isAuthenticated ? <Navigate to="/admin" replace /> : <SignInRedirect />
              }
            />
          </Routes>
        </ThemeProvider>
      </NotificationProvider>
      </SyncStatusProvider>
        </PermissionsProvider>
      </EditionProvider>
    </Router>
  );
}

export default App;
